/**
 * Data Categories Service
 * Manages data categories for data contracts with support for company-specific categories
 *
 * Features:
 * - System-wide and company-specific categories
 * - CRUD operations with proper validation
 * - Category type management
 * - Display order and activation controls
 * - Integration with field definitions
 */

import {
  supabase,
  handleSupabaseArray,
  type Tables,
} from '@xanectech/supabase-client';
import { createLogger } from '@xanectech/lib';
import type {
  DataCategoryType,
  DataCategory,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CategoryFilters,
} from '@xanectech/types';

const logger = createLogger('Data - DataCategoriesService');

type DataCategoryRow = Tables<'data_categories'>;

export class DataCategoriesService {
  private static toDataCategory(row: DataCategoryRow): DataCategory {
    return {
      ...row,
      allows_multiple_entries: row.allows_multiple_entries ?? false,
      color_theme: row.color_theme ?? '',
      company_id: row.company_id ?? null,
      created_at: row.created_at ?? '',
      display_order: row.display_order ?? 0,
      icon: row.icon ?? null,
      is_active: row.is_active ?? false,
      is_mandatory: row.is_mandatory ?? false,
      is_system_category: row.is_system_category ?? false,
      max_entries: row.max_entries ?? undefined,
      updated_at: row.updated_at ?? '',
    };
  }

  /**
   * Get all categories with optional filtering
   */
  static async getCategories(
    filters: CategoryFilters = {},
  ): Promise<DataCategory[]> {
    let query = supabase
      .from('data_categories')
      .select('*')
      .order('display_order', { ascending: true });

    // Apply filters
    if (filters.company_id !== undefined) {
      if (filters.company_id) {
        query = query.eq('company_id', filters.company_id);
      } else {
        query = query.is('company_id', null);
      }
    }

    if (filters.category_type) {
      query = query.eq('category_type', filters.category_type);
    }

    if (filters.is_active !== undefined) {
      query = query.eq('is_active', filters.is_active);
    }

    if (filters.is_system_category !== undefined) {
      query = query.eq('is_system_category', filters.is_system_category);
    }

    if (filters.search_term) {
      query = query.or(
        `name.ilike.%${filters.search_term}%,description.ilike.%${filters.search_term}%`,
      );
    }

    const { data, error } = await query;

    if (error) {
      logger.error('Error fetching categories:', error);
      throw new Error(`Failed to fetch categories: ${error.message}`);
    }

    return handleSupabaseArray(data).map((row) => this.toDataCategory(row));
  }

  /**
   * Get categories available to a specific company (system + company-specific)
   */
  static async getCompanyCategories(
    companyId: string,
  ): Promise<DataCategory[]> {
    const { data, error } = await supabase
      .from('data_categories')
      .select('*')
      .or(`company_id.is.null,company_id.eq.${companyId}`)
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) {
      logger.error('Error fetching company categories:', error);
      throw new Error(`Failed to fetch company categories: ${error.message}`);
    }

    return handleSupabaseArray(data).map((row) => this.toDataCategory(row));
  }

  /**
   * Get a specific category by ID
   */
  static async getCategoryById(categoryId: string): Promise<DataCategory> {
    const { data, error } = await supabase
      .from('data_categories')
      .select('*')
      .eq('id', categoryId)
      .single();

    if (error) {
      logger.error('Error fetching category:', error);
      throw new Error(`Failed to fetch category: ${error.message}`);
    }

    if (!data) {
      throw new Error('Category not found');
    }

    return this.toDataCategory(data);
  }

  /**
   * Create a new category
   */
  static async createCategory(
    categoryData: CreateCategoryRequest,
  ): Promise<DataCategory> {
    // Validate company-specific category requirements
    if (categoryData.company_id && categoryData.category_type !== 'custom') {
      // Allow company-specific categories of any type, but recommend 'custom'
      logger.warn(
        'Company-specific categories should typically use category_type: "custom"',
      );
    }

    const { data, error } = await supabase
      .from('data_categories')
      .insert({
        ...categoryData,
        is_system_category: !categoryData.company_id, // System categories have no company_id
        icon: categoryData.icon || '📄',
        allows_multiple_entries: categoryData.allows_multiple_entries ?? false,
        is_mandatory: categoryData.is_mandatory ?? false,
        display_order: categoryData.display_order ?? 0,
        color_theme: categoryData.color_theme || '#6366f1',
        is_active: true,
      })
      .select()
      .single();

    if (error) {
      logger.error('Error creating category:', error);
      throw new Error(`Failed to create category: ${error.message}`);
    }

    if (!data) {
      throw new Error('No data returned when creating category');
    }

    return this.toDataCategory(data);
  }

  /**
   * Update an existing category
   */
  static async updateCategory(
    categoryId: string,
    updates: UpdateCategoryRequest,
  ): Promise<DataCategory> {
    // First check if the category exists
    const { data: existingCategory, error: checkError } = await supabase
      .from('data_categories')
      .select('id, is_system_category')
      .eq('id', categoryId)
      .single();

    if (checkError) {
      logger.error('Error checking category existence:', checkError);
      throw new Error(`Category not found: ${categoryId}`);
    }

    const { data, error } = await supabase
      .from('data_categories')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', categoryId)
      .select()
      .single();

    if (error) {
      logger.error('Error updating category:', error);
      throw new Error(`Failed to update category: ${error.message}`);
    }

    if (!data) {
      throw new Error(`No data returned when updating category: ${categoryId}`);
    }

    return this.toDataCategory(data);
  }

  /**
   * Delete a category (only non-system categories)
   */
  static async deleteCategory(categoryId: string): Promise<void> {
    // Check if it's a system category
    const category = await this.getCategoryById(categoryId);
    if (category.is_system_category) {
      throw new Error('Cannot delete system categories');
    }

    const { error } = await supabase
      .from('data_categories')
      .delete()
      .eq('id', categoryId);

    if (error) {
      logger.error('Error deleting category:', error);
      throw new Error(`Failed to delete category: ${error.message}`);
    }
  }

  /**
   * Activate/deactivate a category
   */
  static async toggleCategoryStatus(
    categoryId: string,
    isActive: boolean,
  ): Promise<DataCategory> {
    return this.updateCategory(categoryId, { is_active: isActive });
  }

  /**
   * Reorder categories by updating display_order
   */
  static async reorderCategories(
    categoryUpdates: Array<{ id: string; display_order: number }>,
  ): Promise<void> {
    const updates = categoryUpdates.map((update) =>
      supabase
        .from('data_categories')
        .update({
          display_order: update.display_order,
          updated_at: new Date().toISOString(),
        })
        .eq('id', update.id),
    );

    const results = await Promise.all(updates);

    // Check for any errors
    const errors = results.filter((result) => result.error);
    if (errors.length > 0) {
      const errorDetails = errors
        .map((e, i) => ({
          [`error_${i}`]: e.error?.message || 'Unknown error',
        }))
        .reduce((acc, curr) => ({ ...acc, ...curr }), {});
      logger.error('Error reordering categories:', errorDetails);
      throw new Error('Failed to reorder categories');
    }
  }

  /**
   * Get category usage statistics (number of fields, contracts using it)
   */
  static async getCategoryStats(categoryId: string): Promise<{
    field_count: number;
    contract_count: number;
  }> {
    // Count fields in this category
    const { count: fieldCount, error: fieldError } = await supabase
      .from('field_definitions')
      .select('*', { count: 'exact', head: true })
      .eq('category_id', categoryId)
      .eq('is_active', true);

    if (fieldError) {
      logger.error('Error counting fields:', fieldError);
    }

    // Count contracts using fields from this category
    const { count: contractCount, error: contractError } = await supabase
      .from('contract_field_requirements')
      .select('*', { count: 'exact', head: true })
      .eq('category_id', categoryId);

    if (contractError) {
      logger.error('Error counting contracts:', contractError);
    }

    return {
      field_count: fieldCount || 0,
      contract_count: contractCount || 0,
    };
  }

  /**
   * Duplicate a category (useful for creating company-specific versions of system categories)
   */
  static async duplicateCategory(
    categoryId: string,
    companyId: string,
    newName?: string,
  ): Promise<DataCategory> {
    const originalCategory = await this.getCategoryById(categoryId);

    const duplicateData: CreateCategoryRequest = {
      name: newName || `${originalCategory.name} (Copy)`,
      description: originalCategory.description ?? undefined,
      category_type: 'custom', // Company duplicates are typically custom
      icon: originalCategory.icon ?? undefined,
      allows_multiple_entries: originalCategory.allows_multiple_entries,
      max_entries: originalCategory.max_entries,
      is_mandatory: originalCategory.is_mandatory,
      display_order: originalCategory.display_order,
      color_theme: originalCategory.color_theme,
      company_id: companyId,
    };

    return this.createCategory(duplicateData);
  }
}
